import {
    createContext,
    ReactNode,
    Suspense,
    useContext,
    useEffect,
    useState
} from "react";
import { Loader } from "./Loader";
import { cn } from "@/lib/utils";

export type DelayType = "none" | "normal" | "long";

const DELAY_TIMER = 150;
const LONG_DELAY_TIMER = 500;

const LoadingFallbackContext = createContext<{ onFallbackCleanup: () => void }>(
    {
        onFallbackCleanup: () => undefined
    }
);

export const LoadingFallbackProvider = ({
    onFallbackCleanup,
    children
}: {
    onFallbackCleanup: () => void;
    children: ReactNode;
}) => {
    return (
        <LoadingFallbackContext.Provider value={{ onFallbackCleanup }}>
            {children}
        </LoadingFallbackContext.Provider>
    );
};

export const LoadingBoundary = ({
    absolute,
    height,
    delayType,
    children
}: {
    absolute?: boolean;
    height?: number;
    delayType?: DelayType;
    children: ReactNode;
}) => {
    return (
        <Suspense
            fallback={
                <LoadingBoundaryFallback
                    absolute={absolute}
                    height={height}
                    delayType={delayType}
                />
            }
        >
            {children}
        </Suspense>
    );
};

export const LoadingBoundaryFallback = ({
    absolute,
    height,
    delayType
}: {
    absolute?: boolean;
    height?: number;
    delayType?: DelayType;
}) => {
    const [show, setShow] = useState(delayType === "none" ? true : false);
    useEffect(() => {
        if (delayType === "none") {
            return;
        }
        const timer = setTimeout(
            () => {
                setShow(true);
            },
            delayType === "long" ? LONG_DELAY_TIMER : DELAY_TIMER
        );
        return () => {
            clearTimeout(timer);
        };
    }, [delayType]);

    // cleanup callback for when the fallback is unmounted
    const { onFallbackCleanup } = useContext(LoadingFallbackContext);
    useEffect(() => {
        return () => {
            onFallbackCleanup();
        };
    }, [onFallbackCleanup]);

    if (!show) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex items-center justify-center",
                absolute && "absolute inset-0"
            )}
            style={{ minHeight: height }}
        >
            <Loader />
        </div>
    );
};
